import { useFormContext } from "react-hook-form";
import { string } from "yup";

import type { TextAreaProps } from "@/components/Funnel/base/text-area-input";
import { TextAreaInput } from "@/components/Funnel/base/text-area-input";

import { REQUIRED_VALIDATION_MESSAGE } from "./funnel.consts";

type TextFieldProps = TextAreaProps & {
  validationErrorMessage?: string;
};

type HandleValidation = {
  errorMessage?: string;
  required?: boolean;
  value: string;
};

async function handleValidation({
  value,
  required = false,
  errorMessage,
}: HandleValidation): Promise<string | undefined> {
  const schema = required
    ? string()
        .trim()
        .required(errorMessage || REQUIRED_VALIDATION_MESSAGE)
    : string().trim();

  try {
    await schema.validate(value);
  } catch (error) {
    return error instanceof Error ? error.message : "Invalid value";
  }
}

export const TextArea = ({
  name,
  required,
  hint,
  validationErrorMessage,
  onChange,
  ...restProps
}: TextFieldProps) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const hasError = Boolean(errors?.[name]);
  const innerHint = hasError ? errors?.[name]?.message : hint;
  const { onChange: registerOnChange, ...restRegister } = register(name, {
    validate: (value: string) =>
      handleValidation({
        required,
        value,
        errorMessage: validationErrorMessage,
      }),
  });

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    registerOnChange(event);
    onChange?.(event);
  };

  return (
    <TextAreaInput
      maxLength={5000}
      error={hasError}
      hint={innerHint as string}
      onChange={handleChange}
      {...restRegister}
      {...restProps}
    />
  );
};
